import { useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SystemPermissionRequired } from 'src/components/atoms';
import { AuthorisedCaseImporter } from 'src/components/auth/AuthorisedCaseImporter';
import { SettingsTooltip } from 'src/components/molecules';
import { clearSession } from 'src/creators';
import { useCategoryConfigurationRoute, useIsFeatureEnabled } from 'src/hooks';
import { useNotifications } from 'src/hooks/useNotifications';
import type { AppDispatch } from 'src/store';
import { trackCaseImportEnteredEvent } from 'src/utils/analytics/track/caseImport';
import { getInitials } from 'src/utils/getInitials';
import * as Intercom from 'src/utils/intercom';
import { useTheme } from 'styled-components';
import * as Types from '../../../types';
import { FeatureFlag, NavbarVariantEnum } from '../../../types';
import { RequiredFeatureEnabled } from '../../auth';
import { BaseNavbar } from './BaseNavbar';
import {
  AccountNavLink,
  AccountText,
  AccountTextContainer,
  LinkWithBadgeContainer,
  NavBarIconNotificationContainer,
  ProfileContainer,
  StyledNavAvatar,
  StyledNavBarIcon,
  StyledNotificationBadge,
} from './BaseNavbar.styles';
import { NavLink } from './components/NavLink';
import { NavLinkList } from './components/NavLinkList';
import { NotificationsList } from './components/NotificationsList';
import { useNavbarContext } from './NavbarProvider';

type Props = { disableUpload: boolean };

export enum NavbarPermissions {
  NAV_CASES = 'NAV_CASES',
  NAV_USERS = 'NAV_USERS',
  NAV_INSIGHTS = 'NAV_INSIGHTS',
  NAV_ADMIN = 'NAV_ADMIN',
  NAV_DISCLOSURES = 'NAV_DISCLOSURES',
}

const NavMenu = ({
  openUsers,
  openCases,
  openAdmin,
  openInsights,
  disableUpload,
  onOpenUsers,
  onOpenCases,
  onOpenAdmin,
  onOpenInsights,
}: {
  openUsers: boolean;
  openCases: boolean;
  openAdmin: boolean;
  openInsights: boolean;
  disableUpload: boolean;
  onOpenUsers: () => void;
  onOpenCases: () => void;
  onOpenAdmin: () => void;
  onOpenInsights: () => void;
}) => {
  const { isOpen: open, openNavbar } = useNavbarContext();
  const showCaseImportDropdown = AuthorisedCaseImporter();

  const segmentEnabled = useIsFeatureEnabled(
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );

  const isCoiEnabled = useIsFeatureEnabled(
    Types.FeatureFlag.CONFLICT_OF_INTEREST,
  );
  const isSpeakUpEnabled = useIsFeatureEnabled(Types.FeatureFlag.SPEAK_UP);

  const categoryConfigurationRoute = useCategoryConfigurationRoute();

  const state = useSelector(
    (state: Types.State) => ({
      organisationId: state.organisation.organisation?.id || null,
      userId: state.globalUsers.currentUser?.id || null,
      userSystemRole:
        (state.globalUsers.currentUser?.SystemRoles ?? [])
          .map((role) => role.name)
          .join(', ') || null,
    }),
    shallowEqual,
  );

  const onCaseImporterClicked = () => {
    if (segmentEnabled && state.organisationId) {
      trackCaseImportEnteredEvent({
        organisation_id: Number(state.organisationId),
        user_id: String(state.userId),
        user_system_role: state.userSystemRole,
        entry_point: 'nav_import_cases',
      });
    }
  };

  return (
    <>
      <SystemPermissionRequired
        permission={[
          NavbarPermissions.NAV_CASES,
          NavbarPermissions.NAV_DISCLOSURES,
        ]}
      >
        <NavLinkList
          data-testid="cases-navbar"
          text="Cases"
          icon="uil uil-file-alt"
          navOpen={open}
          open={open && openCases}
          onClick={() => {
            openNavbar();
            onOpenCases();
          }}
          tooltipText="Cases"
        >
          {isSpeakUpEnabled && (
            <SystemPermissionRequired permission={NavbarPermissions.NAV_CASES}>
              <NavLink
                data-testid="cases-link-navbar"
                to="/cases"
                text="Manage Cases"
              />
            </SystemPermissionRequired>
          )}
          {isCoiEnabled && (
            <SystemPermissionRequired
              permission={NavbarPermissions.NAV_DISCLOSURES}
            >
              <NavLink
                data-testid="coi-disclosures"
                to="/disclosures"
                text="Manage Disclosures"
              />
            </SystemPermissionRequired>
          )}
          {showCaseImportDropdown && (
            <NavLink
              data-testid="import-cases-link-navbar"
              to="/uploadCases"
              text="Import Cases"
              onClick={onCaseImporterClicked}
            />
          )}
          {showCaseImportDropdown && (
            <NavLink
              data-testid="import-history-link-navbar"
              to="/uploadCasesHistory"
              text="Import History"
            />
          )}
          <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
            <RequiredFeatureEnabled
              requiredFeature={Types.FeatureFlag.DATA_RETENTION}
            >
              <NavLink
                data-testid="deleted-cases-link-navbar"
                to="/deleted-sanitized-cases"
                text="Deleted/sanitized cases"
              />
            </RequiredFeatureEnabled>
          </SystemPermissionRequired>
        </NavLinkList>
      </SystemPermissionRequired>
      <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
        <RequiredFeatureEnabled
          requiredFeature={[
            Types.FeatureFlag.DYNAMIC_QUESTIONS_V1,
            Types.FeatureFlag.CONFLICT_OF_INTEREST,
          ]}
        >
          <NavLink
            data-testid="form-management-navbar"
            to="/form-management"
            text="Forms"
            icon="uil uil-clipboard-notes"
            navOpen={open}
            tooltipText="Forms"
          />
        </RequiredFeatureEnabled>
      </SystemPermissionRequired>
      <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
        <RequiredFeatureEnabled
          requiredFeature={Types.FeatureFlag.FUZZY_MATCHING_NAMED_OFFENDERS}
        >
          <NavLink
            data-testid="fuzzy-match-preview-link-navbar"
            to="/fuzzy-match-preview"
            text="Fuzzy Match Preview"
            icon="uil uil-search"
            navOpen={open}
            tooltipText="Fuzzy Match Preview"
          />
        </RequiredFeatureEnabled>
      </SystemPermissionRequired>
      <SystemPermissionRequired permission={NavbarPermissions.NAV_USERS}>
        {!disableUpload ? (
          <NavLinkList
            icon="uil uil-users-alt"
            data-testid="users-dropdown-navbar"
            text={'Users'}
            open={open && openUsers}
            onClick={() => {
              openNavbar();
              onOpenUsers();
            }}
            navOpen={open}
            tooltipText="Users"
          >
            <NavLink
              data-testid="users-link-navbar"
              to="/users"
              text="Manage Users"
            />

            <NavLink
              data-testid="upload-users-link-navbar"
              to="/uploadUsers"
              text="Upload Users"
            />
          </NavLinkList>
        ) : (
          <NavLink
            data-testid="users-navbar"
            to="/users"
            text="Users"
            icon="uil uil-users-alt"
            tooltipText="Users"
          />
        )}
      </SystemPermissionRequired>
      {isSpeakUpEnabled && (
        <SystemPermissionRequired permission={NavbarPermissions.NAV_INSIGHTS}>
          <NavLinkList
            data-testid="insights-dropdown-navbar"
            text="Insights"
            open={open && openInsights}
            onClick={() => {
              openNavbar();
              onOpenInsights();
            }}
            icon="uil uil-chart-line"
            navOpen={open}
            tooltipText="Insights"
          >
            <NavLink
              data-testid="ethical-health-link-navbar"
              to="/ethical-health"
              text="Ethical Health"
            />
            <NavLink
              data-testid="ethics-operations-link-navbar"
              to="/ethics-operations"
              text="Ethics Operations"
            />
          </NavLinkList>
        </SystemPermissionRequired>
      )}
      <SystemPermissionRequired permission={NavbarPermissions.NAV_ADMIN}>
        <NavLinkList
          data-testid="admin-dropdown-navbar"
          text="Administration"
          open={open && openAdmin}
          onClick={() => {
            openNavbar();
            onOpenAdmin();
          }}
          navOpen={open}
          icon="uil uil-setting"
          tooltipText="Administration"
        >
          <NavLink
            data-testid="settings-link-navbar-tags"
            to="/tags"
            text="Tags"
          />
          <RequiredFeatureEnabled
            requiredFeature={Types.FeatureFlag.CUSTOM_FIELDS}
          >
            <NavLink
              data-testid="settings-link-navbar-custom-fields"
              to="/field-management"
              text="Field management"
            />
          </RequiredFeatureEnabled>
          <RequiredFeatureEnabled
            requiredFeature={[
              Types.FeatureFlag.PUBLIC_REPORTING,
              Types.FeatureFlag.VAULT_TALK,
              Types.FeatureFlag.HRIS_SYNC,
              Types.FeatureFlag.OPR_DEFAULT_ASSIGNEE_ROUTING,
              Types.FeatureFlag.SLACK_REPORTING,
            ]}
          >
            <NavLink
              data-testid="settings-link-navbar-configurations"
              to="/configurations"
              text="Configurations"
            />
          </RequiredFeatureEnabled>
          <NavLink
            data-testid="settings-link-navbar-authentication"
            to="/authentication"
            text="Authentication"
          />
          <RequiredFeatureEnabled
            requiredFeature={Types.FeatureFlag.DATA_INTEGRATION}
          >
            <NavLink
              data-testid="settings-link-navbar-integrations"
              to="/integrations"
              text="Data Integration"
            />
          </RequiredFeatureEnabled>
          <NavLink
            data-testid="settings-link-navbar-categories"
            to={categoryConfigurationRoute}
            text="Category Configuration"
          />
          <RequiredFeatureEnabled
            requiredFeature={Types.FeatureFlag.DATA_RETENTION}
          >
            <NavLink
              data-testid="settings-link-navbar-data-retention"
              to="/data-retention"
              text="Data Retention"
            />
          </RequiredFeatureEnabled>
          <RequiredFeatureEnabled requiredFeature={Types.FeatureFlag.GROUPS}>
            <NavLink
              data-testid="groups-link-navbar"
              to="/groups"
              text="Manage Groups"
            />
          </RequiredFeatureEnabled>
          <RequiredFeatureEnabled requiredFeature={Types.FeatureFlag.ROUTING}>
            <NavLink
              data-testid="settings-link-navbar-routing"
              to="/routing"
              text="Routing"
            />
          </RequiredFeatureEnabled>
        </NavLinkList>
      </SystemPermissionRequired>
    </>
  );
};

const AccountMenu = () => {
  const {
    isOpen: open,
    isSecondaryDrawerOpen,
    setSecondaryDrawerOpen,
  } = useNavbarContext();
  const settingsTooltipRef = useRef<HTMLDivElement>(null);
  const [settingsTooltipRoot, setSettingsTooltipRoot] =
    useState<HTMLElement | null>(null);
  const [settingsTooltip, setSettingsTooltip] = useState(false);

  const currentUser = useSelector(
    (state: Types.State) => state.globalUsers.currentUser,
  );
  const theme = useTheme();
  const { numberOfNotifications } = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const onLogout = () => {
    dispatch(clearSession());
    history.replace('/login');
  };

  return (
    <>
      <AccountNavLink
        variant={NavbarVariantEnum.MANAGER}
        notificationOpen={isSecondaryDrawerOpen}
        data-testid="notifications-list-button"
        onClick={() => setSecondaryDrawerOpen(!isSecondaryDrawerOpen)}
      >
        <LinkWithBadgeContainer
          open={open}
          notification={Boolean(numberOfNotifications)}
        >
          <NavBarIconNotificationContainer
            open={open}
            notification={Boolean(numberOfNotifications)}
          >
            {Boolean(numberOfNotifications) && (
              <StyledNotificationBadge
                open={!open}
                data-testid="notifications-badge-count"
                variant={'custom'}
                text={numberOfNotifications?.toString()}
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.neutral.N0
                    : theme.atlas.semantic.color.type.inverse
                }
                background-color={
                  theme.isVaultVariant
                    ? theme.newPalette.turquoise.T300
                    : theme.atlas.core.color.brand.diligentRed
                }
              />
            )}
            <StyledNavBarIcon className="uil uil-bell" size="20px" />
          </NavBarIconNotificationContainer>
          <AccountText open={open}>Notifications</AccountText>
          {Boolean(numberOfNotifications) && (
            <StyledNotificationBadge
              open={open}
              data-testid="notifications-badge-count"
              variant={'custom'}
              text={numberOfNotifications?.toString()}
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N0
                  : theme.atlas.semantic.color.type.inverse
              }
              background-color={
                theme.isVaultVariant
                  ? theme.newPalette.turquoise.T300
                  : theme.atlas.core.color.brand.diligentRed
              }
            />
          )}
        </LinkWithBadgeContainer>
      </AccountNavLink>
      <AccountNavLink
        variant={NavbarVariantEnum.MANAGER}
        data-testid="settings-link-navbar-technical-support"
        onClick={() => (Intercom.isOpen ? Intercom.hide() : Intercom.show())}
      >
        <StyledNavBarIcon className="uil uil-chat" size="20px" />
        <AccountText open={open}>Technical Support</AccountText>
      </AccountNavLink>

      <div ref={settingsTooltipRef}>
        <ProfileContainer
          variant={NavbarVariantEnum.MANAGER}
          settingsTooltip={settingsTooltip}
          ref={setSettingsTooltipRoot}
          onClick={() => setSettingsTooltip(!settingsTooltip)}
          data-testid="account-button"
        >
          <StyledNavAvatar
            dark={true}
            key={currentUser?.name}
            variant="text"
            text={getInitials(currentUser?.name)}
            size="small"
          />
          {open && (
            <AccountTextContainer open={open}>
              <AccountText open={open}>{currentUser?.name}</AccountText>
              <StyledNavBarIcon
                className="uil uil-ellipsis-v"
                size="20px"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.neutral.N0
                    : theme.atlas.semantic.color.action.secondaryInverse
                        .onSecondaryInverse
                }
              />
            </AccountTextContainer>
          )}
        </ProfileContainer>
        {settingsTooltip && (
          <SettingsTooltip
            root={settingsTooltipRoot as HTMLElement}
            closeTooltip={() => setSettingsTooltip(false)}
            onLogout={onLogout}
          />
        )}
      </div>
    </>
  );
};

export const ReshubNavbar = ({ disableUpload }: Props) => {
  const [openUsers, setOpenUsers] = useState(false);
  const [openCases, setOpenCases] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [openInsights, setOpenInsights] = useState(false);
  const { notifications } = useNotifications();

  const handleToggleNav = () => {
    setOpenUsers(false);
    setOpenAdmin(false);
    setOpenInsights(false);
  };

  return (
    <BaseNavbar
      variant={NavbarVariantEnum.MANAGER}
      topHeader="OVERVIEW"
      bottomHeader="ACCOUNT"
      onToggle={handleToggleNav}
      topNavList={
        <NavMenu
          openUsers={openUsers}
          openCases={openCases}
          openAdmin={openAdmin}
          openInsights={openInsights}
          disableUpload={disableUpload}
          onOpenUsers={() => {
            setOpenUsers(!openUsers);
          }}
          onOpenCases={() => {
            setOpenCases(!openCases);
          }}
          onOpenAdmin={() => {
            setOpenAdmin(!openAdmin);
          }}
          onOpenInsights={() => {
            setOpenInsights(!openInsights);
          }}
        />
      }
      bottomNavList={<AccountMenu />}
      secondaryDrawer={
        <NotificationsList
          notifications={notifications}
          variant={NavbarVariantEnum.MANAGER}
        />
      }
      responsive={false}
    />
  );
};
